import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
// Middleware floating-ui (react-datepicker v6 usa floating-ui, non popper.js):
// shift mantiene il calendario dentro il viewport su mobile, flip lo ribalta se non c'e' spazio.
import { flip, shift } from '@floating-ui/react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { it } from 'date-fns/locale';
import "react-datepicker/dist/react-datepicker.css";
import {
  X,
  Calendar,
  CalendarOff,
  Clock,
  MapPin,
  FileText,
  Video,
  Users,
  Zap,
  Save,
  Loader2,
  ChevronDown,
  CalendarCheck,
  User,
  Mail,
  Phone,
  StickyNote,
  Stethoscope,
  Search,
  UserPlus,
} from 'lucide-react';
import type { CalendarEvent } from '../../lib/types';
import type { BookingEventType } from '../../lib/types/booking';
import { EmailAutoComplete } from '../ui/EmailAutoComplete';
import { useBookingStore } from '../../lib/stores/bookingStore';
import { useCalendarStore } from '../../lib/stores/calendarStore';
import { useClientStore } from '../../lib/stores/clientStore';
import { supabase } from '../../lib/supabase';
import { useApiConfigurationStore } from '../../lib/stores/apiConfigurationStore';
import { BookerCalendar } from '../booking-public/BookerCalendar';
import type { BookingSlot } from '../../lib/types/booking';
import { hasAlfaDocsLink } from '../../lib/services/alfadocsLinks';
import { UnavailabilityForm, type UnavailabilityTarget } from './UnavailabilityDrawer';
import { NotionButton } from '../ui/notion';
import { UserSelectRow } from '../shared/property-rows';
import { useAuth } from '../../lib/auth';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useCalendarSharingStore } from '../../lib/stores/calendarSharingStore';
import { useEnsureSharing } from '../../lib/calendar/team/useEnsureSharing';

// Register Italian locale
registerLocale('it', it);
setDefaultLocale('it');

interface GoogleCalendarOption {
  id: string;
  name: string;
  googleCalendarId: string;
}

interface EventFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  event?: CalendarEvent | null;
  onSave: (data: Omit<CalendarEvent, 'id' | 'created_at' | 'created_by'>) => Promise<void>;
  onBookingCreated?: () => void;
  isGoogleCalendar?: boolean;
  googleCalendars?: GoogleCalendarOption[];
  clientId?: string;
  projectId?: string;
  translations?: any;
  canCreateBooking?: boolean;
  alfadocsActive?: boolean;
  /** Quando valorizzato, il form è in modalità "riprogramma da Google Calendar":
   *  pre-compila i campi prenotazione e, al salvataggio, elimina l'evento Google originale. */
  rescheduleFromGoogle?: { eventId: string; calendarId: string } | null;
  /** Terzo tab "Non disponibile": nello stesso drawer, con l'orario scelto, si segna un'indisponibilità (con la persona). */
  allowUnavailability?: boolean;
}

interface PropertyRowProps {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
  error?: string;
}

const PropertyRow: React.FC<PropertyRowProps> = ({ icon: Icon, label, children, error }) => (
  <div className="py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] last:border-b-0">
    <div className="flex items-start gap-3">
      <div className="flex items-center justify-center w-6 h-6 mt-1">
        <Icon className="w-4 h-4 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide mb-1.5">
          {label}
        </div>
        {children}
        {error && (
          <p className="text-xs text-red-500 mt-1">{error}</p>
        )}
      </div>
    </div>
  </div>
);

const inputClass = "w-full px-2 py-1.5 text-sm text-[#37352F] dark:text-white placeholder-[#787774] dark:placeholder-gray-500 bg-[#FBFBFA] dark:bg-[#3F4447] border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] rounded focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors";

export function EventFormDrawer({
  isOpen,
  onClose,
  event,
  onSave,
  onBookingCreated,
  isGoogleCalendar = false,
  googleCalendars = [],
  clientId,
  projectId,
  translations: t = {},
  canCreateBooking = false,
  alfadocsActive = false,
  rescheduleFromGoogle = null,
  allowUnavailability = false,
}: EventFormDrawerProps) {
  // Mode: event or booking
  const [eventMode, setEventMode] = useState<'event' | 'booking' | 'unavailable'>('event');
  // Tab "Non disponibile": l'orario del form al momento del clic (il modulo riparte da questo)
  const [unavailabilityTarget, setUnavailabilityTarget] = useState<UnavailabilityTarget>({});
  const [unavailabilitySaving, setUnavailabilitySaving] = useState(false);
  // "Calendario di" in cima (Evento e Non disponibile): solo con il permesso "Segna le indisponibilità dei colleghi"
  const { user: authUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canChooseOwner = allowUnavailability && hasPermission('calendar_others', 'schedule');
  useEnsureSharing(canChooseOwner);
  const sharingPeople = useCalendarSharingStore(s => s.people);
  const ownerOptions = useMemo(() => sharingPeople.map(p => ({ id: p.id, full_name: p.name })), [sharingPeople]);
  const [ownerId, setOwnerId] = useState('');
  useEffect(() => { if (isOpen) setOwnerId(authUser?.id || ''); }, [isOpen, authUser?.id]);
  // Reschedule da Google: se true, al salvataggio elimina l'evento Google originale
  const [deleteOriginalGoogle, setDeleteOriginalGoogle] = useState(true);
  // Tipo prenotazione suggerito dal titolo dell'evento Google ('secondo'|'consulenza'|'primo')
  const [rescheduleSuggestedTypeKw, setRescheduleSuggestedTypeKw] = useState('');

  // ─── Event form state ─────────────────────────────────────
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [endTime, setEndTime] = useState<Date>(new Date(new Date().setHours(new Date().getHours() + 1)));
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [addGoogleMeet, setAddGoogleMeet] = useState(false);
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [saveToGoogleCalendar, setSaveToGoogleCalendar] = useState(false);
  const [selectedGoogleCalendarId, setSelectedGoogleCalendarId] = useState<string>('');
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSaving, setIsSaving] = useState(false);

  // ─── Booking form state ───────────────────────────────────
  const [bookingEventTypeId, setBookingEventTypeId] = useState('');
  const [bookingAssignedUserId, setBookingAssignedUserId] = useState('');
  const [bookerName, setBookerName] = useState('');
  const [bookerEmail, setBookerEmail] = useState('');
  const [bookerPhone, setBookerPhone] = useState('');
  const [bookerNotes, setBookerNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [bookingSelectedDate, setBookingSelectedDate] = useState<string | null>(null);
  const [bookingSelectedSlot, setBookingSelectedSlot] = useState<BookingSlot | null>(null);
  // Orario manuale (fuori griglia) — es. ritocchi a 45 min di distanza
  const [manualBookingTime, setManualBookingTime] = useState('');
  // Durata manuale (min) — default = durata dell'event type selezionato
  const [manualBookingDuration, setManualBookingDuration] = useState('');
  const [bookingClientId, setBookingClientId] = useState<string | null>(null);
  const [bookingClientSearch, setBookingClientSearch] = useState('');
  const [bookingClientResults, setBookingClientResults] = useState<any[]>([]);
  const [bookingClientSearching, setBookingClientSearching] = useState(false);
  const [bookingClientDropdownOpen, setBookingClientDropdownOpen] = useState(false);
  const [bookingManualEntry, setBookingManualEntry] = useState(false);
  const [ignoreConflict, setIgnoreConflict] = useState(false);
  const bookingClientSearchRef = useRef<HTMLInputElement>(null);
  const bookingClientDropdownRef = useRef<HTMLDivElement>(null);

  // ─── AlfaDocs state ──────────────────────────────────────
  const [alfadocsEventType, setAlfadocsEventType] = useState<string | null>(null);
  const [alfadocsClientId, setAlfadocsClientId] = useState<string>(clientId || '');
  const [alfadocsClients, setAlfadocsClients] = useState<{ value: string; label: string }[]>([]);

  // Load AlfaDocs appointments config (label/tipi form) dalla connessione attiva più vecchia
  // dell'org — useApiConfigurationStore.configurations è ordinata created_at DESC, quindi
  // riordiniamo ASC qui prima del find (v1: coerente con DealActivityForm/altri drawer).
  const alfadocsApptConfig = useMemo(() => {
    if (!alfadocsActive) return null;
    try {
      const configs = useApiConfigurationStore.getState().configurations;
      const oldestActive = configs
        .filter(c => c.service_name === 'alfadocs' && c.is_active)
        .sort((a: any, b: any) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())[0];
      return oldestActive?.configuration?.appointments_config || null;
    } catch {
      return null;
    }
  }, [alfadocsActive]);

  // Load AlfaDocs-linked clients for patient selector (multi-studio: qualunque studio collegato)
  useEffect(() => {
    if (!alfadocsActive || !alfadocsEventType) return;
    (async () => {
      const { data } = await supabase
        .from('clients')
        .select('id, business_name, metadata')
        .eq('lead_source', 'alfadocs')
        .order('business_name');
      if (data) {
        setAlfadocsClients(data
          .filter((c: any) => hasAlfaDocsLink(c.metadata))
          .map((c: any) => ({ value: c.id, label: c.business_name || 'Senza nome' }))
        );
      }
    })();
  }, [alfadocsActive, alfadocsEventType]);

  const alfadocsLabel = alfadocsApptConfig?.label || 'Appuntamento clinico';
  const alfadocsTypes = (alfadocsApptConfig?.types || [
    { value: 'visita', label: 'Visita studio', enabled: true },
    { value: 'operazione', label: 'Operazione', enabled: true },
    { value: 'telehealth', label: 'Telehealth', enabled: true },
  ]).filter((t: any) => t.enabled);
  const alfadocsSyncEnabled = alfadocsApptConfig?.sync_enabled !== false;

  const { eventTypes, fetchEventTypes, createInternalBooking, fetchPublicSlots, publicSlots, publicAllSlots, publicBusyIntervals, publicSlotsLoading } = useBookingStore();
  const deleteGoogleEvent = useCalendarStore((s) => s.deleteGoogleEvent);
  const { searchClientsAndLeads } = useClientStore();

  // Debounced client search for booking form
  const bookingClientSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!bookingClientSearch || bookingClientSearch.length < 2) {
      setBookingClientResults([]);
      return;
    }
    if (bookingClientSearchTimerRef.current) clearTimeout(bookingClientSearchTimerRef.current);
    bookingClientSearchTimerRef.current = setTimeout(async () => {
      setBookingClientSearching(true);
      try {
        const results = await searchClientsAndLeads(bookingClientSearch);
        setBookingClientResults(results);
        setBookingClientDropdownOpen(true);
      } catch { setBookingClientResults([]); }
      setBookingClientSearching(false);
    }, 300);
    return () => { if (bookingClientSearchTimerRef.current) clearTimeout(bookingClientSearchTimerRef.current); };
  }, [bookingClientSearch, searchClientsAndLeads]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bookingClientDropdownRef.current && !bookingClientDropdownRef.current.contains(e.target as Node)) {
        setBookingClientDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Refs per prevenire loop infiniti con le dipendenze array
  const isInitializedRef = useRef(false);
  const prevGoogleCalendarsRef = useRef<string>('');
  const prevIsOpenRef = useRef(false);
  const prevEventIdRef = useRef<string | null>(null);

  // Fetch event types when booking mode is selected
  useEffect(() => {
    if (eventMode === 'booking' && isOpen && eventTypes.length === 0) {
      fetchEventTypes();
    }
  }, [eventMode, isOpen]);

  // Initialize form with event data
  useEffect(() => {
    const googleCalendarsKey = googleCalendars.map(c => c.googleCalendarId).join(',');
    const googleCalendarsChanged = googleCalendarsKey !== prevGoogleCalendarsRef.current;
    const isOpenChanged = isOpen !== prevIsOpenRef.current;
    const eventChanged = (event?.id || null) !== prevEventIdRef.current;

    prevGoogleCalendarsRef.current = googleCalendarsKey;
    prevIsOpenRef.current = isOpen;
    prevEventIdRef.current = event?.id || null;

    if (!isOpen) {
      isInitializedRef.current = false;
      return;
    }

    if (isInitializedRef.current && !isOpenChanged && !eventChanged) {
      if (googleCalendarsChanged && googleCalendars.length > 0 && !selectedGoogleCalendarId) {
        setSelectedGoogleCalendarId(googleCalendars[0].googleCalendarId);
      }
      return;
    }

    if (event) {
      setEventMode('event');
      setTitle(event.title || '');
      setDescription(event.description || '');
      setStartTime(event.start_time ? new Date(event.start_time) : new Date());
      setEndTime(event.end_time ? new Date(event.end_time) : new Date(new Date().setHours(new Date().getHours() + 1)));
      setAllDay(event.all_day || false);
      setLocation(event.location || '');
      setAddGoogleMeet(!!event.conference_data);
      // Reschedule da Google: pre-compila anche i campi prenotazione (data, orario, durata, note)
      // + estrae Telefono/Email/Nome dalla descrizione (formato widget: "Phone:- ... Email:- ...")
      // così la tab "Prenotazione" parte già valorizzata dall'evento originale.
      if (rescheduleFromGoogle && event.start_time) {
        const s = new Date(event.start_time);
        const e = event.end_time ? new Date(event.end_time) : new Date(s.getTime() + 30 * 60 * 1000);
        const pad = (n: number) => String(n).padStart(2, '0');
        setBookingSelectedDate(`${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`);
        setManualBookingTime(`${pad(s.getHours())}:${pad(s.getMinutes())}`);
        setManualBookingDuration(String(Math.max(5, Math.round((e.getTime() - s.getTime()) / 60000))));

        const desc = event.description || '';
        // Telefono: "Phone:- 333 225 6937" → +393332256937
        const phoneMatch = desc.match(/phone[:\-\s]*([+\d][\d\s().\-]{5,})/i);
        let phone = '';
        if (phoneMatch) {
          const digits = phoneMatch[1].replace(/\D/g, '');
          if (digits.length >= 9) {
            phone = digits.startsWith('39') ? `+${digits}`
              : (digits.length === 10 && digits.startsWith('3') ? `+39${digits}` : `+${digits}`);
          }
        }
        // Email: "Email:- xxx@yyy"
        const emailMatch = desc.match(/email[:\-\s]*([^\s@]+@[^\s@]+\.[^\s@]+)/i);
        const email = emailMatch ? emailMatch[1] : '';
        // Nome pulito dal titolo (togli parole-trattamento, importi, numeri, parentesi)
        const rawTitle = (event.title || '').trim();
        const cleaned = rawTitle
          .replace(/\britocco\b/gi, '')
          .replace(/\b(sopracciglia|labbra|eye-?liner|laser|microblading|micro|laminazione|trico|ascella|viso|consulenza|cliente tattoo|tattoo)\b/gi, '')
          .replace(/acc\s*\d+\s*€?/gi, '')
          .replace(/\bx\s*\d+\b/gi, '')
          .replace(/\(.*?\)/g, '')
          .replace(/[+€]/g, ' ')
          .replace(/\d+/g, ' ')
          .replace(/\s{2,}/g, ' ')
          .trim();
        setBookerName(cleaned || rawTitle);
        // SEMPRE assegnati (anche se vuoti) per NON ereditare i dati della conversione
        // precedente: senza reset, un evento senza email "teneva" l'email del precedente
        // → autolink collegava al cliente sbagliato.
        setBookerPhone(phone);
        setBookerEmail(email);
        setBookerNotes(desc);
        setBookingClientId(null);
        setBookingEventTypeId('');
        setBookingSelectedSlot(null);
        setDeleteOriginalGoogle(true);
        // Tipo prenotazione suggerito dal titolo
        const tl = rawTitle.toLowerCase();
        setRescheduleSuggestedTypeKw(/ritocco/.test(tl) ? 'secondo' : (/consulenza/.test(tl) ? 'consulenza' : 'primo'));
      }
    } else {
      setEventMode('event');
      setTitle('');
      setDescription('');
      setStartTime(new Date());
      setEndTime(new Date(new Date().setHours(new Date().getHours() + 1)));
      setAllDay(false);
      setLocation('');
      setAddGoogleMeet(false);
      setInviteEmails([]);
      setSaveToGoogleCalendar(false);
      if (googleCalendars.length > 0) {
        setSelectedGoogleCalendarId(googleCalendars[0].googleCalendarId);
      } else {
        setSelectedGoogleCalendarId('');
      }
      // Reset booking fields
      setBookingEventTypeId('');
      setBookerName('');
      setBookerEmail('');
      setBookerPhone('');
      setBookerNotes('');
      setInternalNotes('');
      setBookingSelectedDate(null);
      setBookingSelectedSlot(null);
      setManualBookingTime('');
      setManualBookingDuration('');
      setRescheduleSuggestedTypeKw('');
      setBookingClientId(null);
      setBookingClientSearch('');
      setBookingClientResults([]);
      setBookingManualEntry(false);
      setAlfadocsEventType(null);
    }
    setErrors({});
    isInitializedRef.current = true;
  }, [event, isOpen, googleCalendars, selectedGoogleCalendarId]);

  // Reschedule da Google: quando gli event types sono caricati, pre-seleziona il tipo
  // suggerito dal titolo ('secondo'/'consulenza'/'primo' → match sul titolo dell'event type).
  useEffect(() => {
    if (!rescheduleFromGoogle || !rescheduleSuggestedTypeKw) return;
    if (eventTypes.length === 0 || bookingEventTypeId) return;
    const match = eventTypes.find((et: BookingEventType) => et.title?.toLowerCase().includes(rescheduleSuggestedTypeKw));
    if (match) setBookingEventTypeId(match.id);
  }, [rescheduleFromGoogle, rescheduleSuggestedTypeKw, eventTypes, bookingEventTypeId]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Auto-adjust end time when all-day changes
  useEffect(() => {
    if (allDay) {
      const newEndTime = new Date(startTime);
      newEndTime.setHours(23, 59, 59, 999);
      setEndTime(newEndTime);
    }
  }, [allDay, startTime]);

  // Auto-enable saveToGoogleCalendar when Google Meet is enabled
  useEffect(() => {
    if (addGoogleMeet) {
      setSaveToGoogleCalendar(true);
    }
  }, [addGoogleMeet]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (eventMode === 'event') {
      if (!title.trim()) {
        newErrors.title = t.titleRequired || 'Il titolo è obbligatorio';
      }
      if (endTime <= startTime && !allDay) {
        newErrors.endTime = t.endTimeError || "L'ora di fine deve essere successiva all'inizio";
      }
    } else {
      if (!bookingEventTypeId) {
        newErrors.eventType = 'Seleziona un tipo di prenotazione';
      }
      if (!bookingSelectedSlot) {
        newErrors.slot = 'Seleziona data e orario';
      }
      if (!bookerName.trim()) {
        newErrors.bookerName = 'Il nome è obbligatorio';
      }
      if (!bookerEmail.trim()) {
        newErrors.bookerEmail = "L'email è obbligatoria";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookerEmail)) {
        newErrors.bookerEmail = 'Email non valida';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    // Reschedule da Google: elimina l'evento originale dopo aver creato il nuovo evento/prenotazione.
    const maybeDeleteOriginalGoogle = async () => {
      if (rescheduleFromGoogle && deleteOriginalGoogle) {
        try {
          await deleteGoogleEvent(rescheduleFromGoogle.eventId, rescheduleFromGoogle.calendarId);
        } catch (e) {
          console.warn('[EventFormDrawer] Impossibile eliminare evento Google originale:', e);
        }
      }
    };
    try {
      if (eventMode === 'booking') {
        // Create booking
        const booking = await createInternalBooking({
          event_type_id: bookingEventTypeId,
          start_time: bookingSelectedSlot!.start,
          end_time: bookingSelectedSlot!.end,
          booker_name: bookerName,
          booker_email: bookerEmail,
          booker_phone: bookerPhone || undefined,
          booker_notes: bookerNotes || undefined,
          booker_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          assigned_to: bookingAssignedUserId || selectedEventType?.assigned_user_id || undefined,
          internal_notes: internalNotes || undefined,
          client_id: bookingClientId || undefined,
        });

        if (booking) {
          await maybeDeleteOriginalGoogle();
          onBookingCreated?.();
          onClose();
        }
      } else {
        // Create event (existing logic)
        const ownerChosen = canChooseOwner && !isEditing && !!ownerId;
        const adjustedStartTime = new Date(startTime);
        const adjustedEndTime = new Date(endTime);

        if (allDay) {
          adjustedStartTime.setHours(0, 0, 0, 0);
          adjustedEndTime.setHours(23, 59, 59, 999);
        }

        const conferenceData = addGoogleMeet && isGoogleCalendar ? {
          createRequest: {
            requestId: `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            conferenceSolutionKey: { type: 'hangoutsMeet' }
          }
        } : undefined;

        await onSave({
          title,
          description,
          start_time: adjustedStartTime.toISOString(),
          end_time: adjustedEndTime.toISOString(),
          all_day: allDay,
          location,
          client_id: alfadocsEventType ? alfadocsClientId || clientId : clientId,
          project_id: projectId,
          // Nel calendario della persona scelta in "Calendario di" (solo con il permesso): assigned_to + metadata.calendar_owner_id
          ...(ownerChosen ? { assigned_to: ownerId } : {}),
          conference_data: conferenceData,
          attendees: inviteEmails.map(email => ({ email })),
          save_to_google: saveToGoogleCalendar,
          google_calendar_id: saveToGoogleCalendar ? selectedGoogleCalendarId : undefined,
          ...(alfadocsEventType || ownerChosen ? {
            metadata: {
              ...(alfadocsEventType ? { create_on_alfadocs: true, alfadocs_event_type: alfadocsEventType } : {}),
              ...(ownerChosen ? { calendar_owner_id: ownerId } : {}),
            },
          } : {}),
        } as any);

        await maybeDeleteOriginalGoogle();
        onClose();
      }
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartTimeChange = (date: Date) => {
    setStartTime(date);
    if (date > endTime) {
      const newEndTime = new Date(date.getTime());
      newEndTime.setHours(newEndTime.getHours() + 1);
      setEndTime(newEndTime);
    }
  };

  // "Modifica" solo se l'evento ha un id reale. Il tap su uno slot del calendario
  // passa date pre-compilate (event senza id): deve restare CREAZIONE e mostrare
  // i tab Evento/Prenotazione, esattamente come il pulsante "+".
  const isEditing = !!event?.id;
  const selectedEventType = eventTypes.find((et: BookingEventType) => et.id === bookingEventTypeId);

  // ── Rilevamento conflitti orario (per il campo orario manuale) ──
  // Gli slot occupati sono quelli marcati available:false in allSlots dal backend
  // Conflict check per orari manuali e slot selezionati.
  // Usa busyIntervals (booking reali ± buffer) se disponibili dall'edge function;
  // fallback agli allSlots non disponibili (proxy meno preciso basato sulla griglia).
  const findSlotConflict = useMemo(() => {
    const intervals: Array<{ start: string; end: string }> =
      publicBusyIntervals.length > 0
        ? publicBusyIntervals
        : publicAllSlots.filter((s) => s.available === false);
    return (startISO: string, endISO: string): { start: string; end: string } | null => {
      const s = new Date(startISO).getTime();
      const e = new Date(endISO).getTime();
      if (isNaN(s) || isNaN(e)) return null;
      return (
        intervals.find(
          (iv) => new Date(iv.start).getTime() < e && new Date(iv.end).getTime() > s
        ) || null
      );
    };
  }, [publicBusyIntervals, publicAllSlots]);

  // Intervallo dell'orario manuale attualmente digitato (prima di "Imposta")
  const manualInterval = useMemo(() => {
    if (!bookingSelectedDate || !manualBookingTime) return null;
    const start = new Date(`${bookingSelectedDate}T${manualBookingTime}:00`);
    if (isNaN(start.getTime())) return null;
    const durationMin = parseInt(manualBookingDuration) || selectedEventType?.duration_minutes || 30;
    const end = new Date(start.getTime() + durationMin * 60 * 1000);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [bookingSelectedDate, manualBookingTime, manualBookingDuration, selectedEventType]);

  const manualConflict = manualInterval ? findSlotConflict(manualInterval.start, manualInterval.end) : null;
  const selectedSlotConflict = bookingSelectedSlot
    ? findSlotConflict(bookingSelectedSlot.start, bookingSelectedSlot.end)
    : null;
  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[10000]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-y-0 right-0 w-[480px] max-w-full bg-white dark:bg-[#2F3437] border-l border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-xl z-[10001] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#787774] dark:text-gray-400" />
                <span className="text-sm font-medium text-[#37352F] dark:text-white">
                  {isEditing ? (t.editEvent || 'Modifica Evento') : eventMode === 'unavailable' ? 'Non disponibile' : (t.newEvent || 'Nuovo Evento')}
                </span>
                {isGoogleCalendar && (
                  <span className="px-2 py-0.5 bg-[rgba(35,131,226,0.1)] text-[#2383E2] text-[10px] font-medium rounded">
                    Google Calendar
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-[#787774] dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4">
              {/* ── "Calendario di": per chi ha il permesso, l'evento o l'indisponibilità va nel calendario della persona scelta ── */}
              {!isEditing && canChooseOwner && eventMode !== 'booking' && (
                <div className="py-2 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                  <UserSelectRow
                    icon={<User className="h-3 w-3" strokeWidth={1.5} />}
                    label="Calendario di"
                    value={ownerId}
                    onChange={v => setOwnerId(v || authUser?.id || '')}
                    users={ownerOptions}
                  />
                </div>
              )}

              {/* ── Mode Selector (solo per nuovi eventi: Evento, Prenotazione se canCreateBooking, Non disponibile se allowUnavailability) ── */}
              {!isEditing && (canCreateBooking || allowUnavailability) && (
                <div className="py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                  <div className="flex gap-1 p-0.5 bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] rounded-lg">
                    <button
                      onClick={() => setEventMode('event')}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${
                        eventMode === 'event'
                          ? 'bg-white dark:bg-[#3F4447] text-[#37352F] dark:text-white shadow-sm'
                          : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                      }`}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      Evento
                    </button>
                    {canCreateBooking && (
                      <button
                        onClick={() => setEventMode('booking')}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${
                          eventMode === 'booking'
                            ? 'bg-white dark:bg-[#3F4447] text-[#37352F] dark:text-white shadow-sm'
                            : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                        }`}
                      >
                        <CalendarCheck className="w-3.5 h-3.5" />
                        Prenotazione
                      </button>
                    )}
                    {allowUnavailability && (
                      <button
                        onClick={() => {
                          if (eventMode !== 'unavailable') setUnavailabilityTarget({ start: startTime, end: endTime, allDay });
                          setEventMode('unavailable');
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 text-xs font-medium rounded-md transition-colors ${
                          eventMode === 'unavailable'
                            ? 'bg-white dark:bg-[#3F4447] text-[#37352F] dark:text-white shadow-sm'
                            : 'text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white'
                        }`}
                      >
                        <CalendarOff className="w-3.5 h-3.5" />
                        Non disponibile
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ═══════════════════════════════════════════════ */}
              {/* BOOKING MODE                                   */}
              {/* ═══════════════════════════════════════════════ */}
              {eventMode === 'unavailable' && !isEditing ? (
                <div className="py-3">
                  <UnavailabilityForm target={unavailabilityTarget} formId="event-drawer-unavailability" onDone={onClose} onSavingChange={setUnavailabilitySaving} personId={canChooseOwner ? ownerId : undefined} />
                </div>
              ) : eventMode === 'booking' && !isEditing ? (
                <>
                  {/* Event Type Selector */}
                  <PropertyRow icon={CalendarCheck} label="Tipo di prenotazione" error={errors.eventType}>
                    <div className="relative">
                      <select
                        value={bookingEventTypeId}
                        onChange={(e) => {
                          const etId = e.target.value;
                          setBookingEventTypeId(etId);
                          setBookingSelectedDate(null);
                          setBookingSelectedSlot(null);
                          // Auto-select first assigned user
                          const et = eventTypes.find((t: BookingEventType) => t.id === etId);
                          const users = et?.assigned_users || [];
                          setBookingAssignedUserId(users.length > 0 ? users[0].id : (et?.assigned_user_id || ''));
                        }}
                        className={`${inputClass} appearance-none cursor-pointer pr-8`}
                      >
                        <option value="">Seleziona tipo...</option>
                        {eventTypes.filter((et: BookingEventType) => et.is_active).map((et: BookingEventType) => (
                          <option key={et.id} value={et.id}>
                            {et.title} ({et.duration_minutes} min)
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#787774] pointer-events-none" />
                    </div>
                  </PropertyRow>

                  {/* Assigned User — select if multiple, readonly if single */}
                  {selectedEventType && (() => {
                    const assignedUsers = selectedEventType.assigned_users || [];
                    if (assignedUsers.length > 1) {
                      return (
                        <PropertyRow icon={Users} label="Assegnato a">
                          <div className="relative">
                            <select
                              value={bookingAssignedUserId}
                              onChange={(e) => {
                                setBookingAssignedUserId(e.target.value);
                                setBookingSelectedDate(null);
                                setBookingSelectedSlot(null);
                              }}
                              className={`${inputClass} appearance-none cursor-pointer pr-8`}
                            >
                              {assignedUsers.map((u: any) => (
                                <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#787774] pointer-events-none" />
                          </div>
                        </PropertyRow>
                      );
                    }
                    const singleUser = assignedUsers[0] || selectedEventType.assigned_user;
                    if (singleUser) {
                      return (
                        <PropertyRow icon={Users} label="Assegnato a">
                          <span className="text-sm text-[#37352F] dark:text-white">
                            {singleUser.full_name || singleUser.email}
                          </span>
                        </PropertyRow>
                      );
                    }
                    return null;
                  })()}

                  {/* Date & Time via slot selection */}
                  {bookingEventTypeId && (
                    <PropertyRow icon={Clock} label="Data e ora" error={errors.slot}>
                      {bookingSelectedSlot ? (
                        <div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-[#37352F] dark:text-white capitalize">
                              {new Date(bookingSelectedSlot.start).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                              {' '}
                              {new Date(bookingSelectedSlot.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                              {' - '}
                              {new Date(bookingSelectedSlot.end).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <button
                              onClick={() => { setBookingSelectedSlot(null); setBookingSelectedDate(null); setManualBookingTime(''); setManualBookingDuration(''); setIgnoreConflict(false); }}
                              className="text-xs text-[#2383E2] hover:underline ml-2"
                            >
                              Cambia
                            </button>
                          </div>
                          {selectedSlotConflict && !ignoreConflict && (
                            <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                              <div className="flex items-start gap-2 mb-2">
                                <span>⚠️</span>
                                <span>Questo orario si sovrappone a un altro appuntamento ({fmtTime(selectedSlotConflict.start)}–{fmtTime(selectedSlotConflict.end)}).</span>
                              </div>
                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={ignoreConflict}
                                  onChange={(e) => setIgnoreConflict(e.target.checked)}
                                  className="w-3.5 h-3.5 accent-amber-600"
                                />
                                <span>Procedi comunque (ignora conflitto)</span>
                              </label>
                            </div>
                          )}
                          {selectedSlotConflict && ignoreConflict && (
                            <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700/50 px-3 py-2 text-xs text-blue-800 dark:text-blue-300">
                              <span>ℹ️</span>
                              <span>Conflitto ignorato — puoi procedere con il salvataggio.</span>
                            </div>
                          )}
                        </div>
                      ) : !bookingSelectedDate ? (
                        <div className="mt-1">
                          <BookerCalendar
                            selectedDate={bookingSelectedDate}
                            onChange={(dateStr: string) => {
                              setBookingSelectedDate(dateStr);
                              fetchPublicSlots(bookingEventTypeId, dateStr, Intl.DateTimeFormat().resolvedOptions().timeZone, bookingAssignedUserId || undefined);
                            }}
                            minDate={new Date()}
                          />
                        </div>
                      ) : (
                        <div className="mt-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm text-[#787774] capitalize">
                              {new Date(bookingSelectedDate + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </p>
                            <button
                              onClick={() => setBookingSelectedDate(null)}
                              className="text-xs text-[#2383E2] hover:underline"
                            >
                              Cambia data
                            </button>
                          </div>
                          {publicSlotsLoading ? (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-5 h-5 animate-spin text-[#787774]" />
                            </div>
                          ) : (publicAllSlots.length === 0 && publicSlots.length === 0) ? (
                            <div className="text-center py-6 text-[#787774] text-sm">
                              Nessun orario disponibile per questa data.
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                              {(publicAllSlots.length > 0 ? publicAllSlots : publicSlots).map((slot) => {
                                const isAvailable = publicSlots.some(s => s.start === slot.start);
                                return (
                                  <button
                                    key={slot.start}
                                    onClick={() => isAvailable ? setBookingSelectedSlot(slot) : undefined}
                                    disabled={!isAvailable}
                                    className={`py-2 text-sm font-medium rounded-lg border transition-all ${
                                      isAvailable
                                        ? 'border-[rgba(55,53,47,0.16)] hover:bg-[rgba(55,53,47,0.04)] cursor-pointer'
                                        : 'border-[rgba(55,53,47,0.06)] bg-[rgba(55,53,47,0.04)] text-[#787774]/50 cursor-not-allowed line-through'
                                    }`}
                                  >
                                    {new Date(slot.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/* Orario + durata manuali: per orari fuori griglia (es. ritocchi a 45 min) */}
                          <div className="mt-3 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
                            <label className="block text-xs text-[#787774] mb-1.5">Oppure inserisci orario e durata manuali</label>
                            <div className="flex items-end gap-2">
                              <div className="flex-1 min-w-0">
                                <span className="block text-[11px] text-[#787774] mb-1">Ora inizio</span>
                                <input
                                  type="time"
                                  value={manualBookingTime}
                                  onChange={(e) => setManualBookingTime(e.target.value)}
                                  className={inputClass}
                                />
                              </div>
                              <div className="w-24 shrink-0">
                                <span className="block text-[11px] text-[#787774] mb-1">Durata (min)</span>
                                <input
                                  type="number"
                                  min={5}
                                  step={5}
                                  value={manualBookingDuration}
                                  placeholder={`${selectedEventType?.duration_minutes ?? 30}`}
                                  onChange={(e) => setManualBookingDuration(e.target.value)}
                                  className={inputClass}
                                />
                              </div>
                              <button
                                type="button"
                                disabled={!manualBookingTime || !bookingSelectedDate}
                                onClick={() => {
                                  if (!bookingSelectedDate || !manualBookingTime) return;
                                  const start = new Date(`${bookingSelectedDate}T${manualBookingTime}:00`);
                                  if (isNaN(start.getTime())) return;
                                  const durationMin = parseInt(manualBookingDuration) || selectedEventType?.duration_minutes || 30;
                                  const end = new Date(start.getTime() + durationMin * 60 * 1000);
                                  setBookingSelectedSlot({ start: start.toISOString(), end: end.toISOString() });
                                }}
                                className="shrink-0 px-3 py-2 text-sm font-medium rounded-lg bg-[#2383E2] text-white hover:bg-[#1a6fc4] disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                Imposta
                              </button>
                            </div>
                            <p className="text-[11px] text-[#787774] mt-1">Per orari fuori dalla griglia (es. ritocchi a 45 min). Durata predefinita: quella del tipo di prenotazione.</p>
                            {manualConflict && !ignoreConflict && (
                              <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                <div className="flex items-start gap-2 mb-2">
                                  <span>⚠️</span>
                                  <span>L'orario {manualInterval && fmtTime(manualInterval.start)} si sovrappone a un appuntamento già esistente ({fmtTime(manualConflict.start)}–{fmtTime(manualConflict.end)}).</span>
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={ignoreConflict}
                                    onChange={(e) => setIgnoreConflict(e.target.checked)}
                                    className="w-3.5 h-3.5 accent-amber-600"
                                  />
                                  <span>Procedi comunque (ignora conflitto)</span>
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </PropertyRow>
                  )}

                  {/* Client/Lead Selector */}
                  <PropertyRow icon={Users} label="Cliente">
                    {bookingClientId && !bookingManualEntry ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#37352F] dark:text-white">
                          {bookerName} {bookerEmail && <span className="text-[#787774]">({bookerEmail})</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setBookingClientId(null);
                            setBookerName('');
                            setBookerEmail('');
                            setBookerPhone('');
                            setBookingClientSearch('');
                            setBookingManualEntry(false);
                          }}
                          className="text-xs text-[#2383E2] hover:underline ml-2"
                        >
                          Cambia
                        </button>
                      </div>
                    ) : bookingManualEntry ? (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#787774]">Inserimento manuale</span>
                        <button
                          type="button"
                          onClick={() => {
                            setBookingManualEntry(false);
                            setBookerName('');
                            setBookerEmail('');
                            setBookerPhone('');
                            setBookingClientSearch('');
                          }}
                          className="text-xs text-[#2383E2] hover:underline ml-2"
                        >
                          Cerca cliente
                        </button>
                      </div>
                    ) : (
                      <div className="relative" ref={bookingClientDropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#787774]" />
                          <input
                            ref={bookingClientSearchRef}
                            type="text"
                            value={bookingClientSearch}
                            onChange={(e) => setBookingClientSearch(e.target.value)}
                            onFocus={() => bookingClientResults.length > 0 && setBookingClientDropdownOpen(true)}
                            placeholder="Cerca cliente o lead..."
                            className={`${inputClass} pl-8`}
                          />
                          {bookingClientSearching && (
                            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#787774] animate-spin" />
                          )}
                        </div>
                        {/* Dropdown results */}
                        {bookingClientDropdownOpen && bookingClientResults.length > 0 && (
                          <div className="absolute z-50 mt-1 w-full bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {bookingClientResults.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setBookingClientId(client.id);
                                  setBookerName(client.business_name || '');
                                  setBookerEmail(client.email || '');
                                  setBookerPhone(client.phone || '');
                                  setBookingClientSearch('');
                                  setBookingClientDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-[#3F4447] transition-colors flex items-center gap-2"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-[#37352F] dark:text-white truncate">
                                    {client.business_name || 'Senza nome'}
                                    {client.client_state?.is_lead && (
                                      <span className="ml-1.5 text-[10px] font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">Lead</span>
                                    )}
                                  </div>
                                  {client.email && (
                                    <div className="text-xs text-[#787774] truncate">{client.email}</div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {/* No results + manual entry */}
                        {bookingClientDropdownOpen && bookingClientSearch.length >= 2 && !bookingClientSearching && bookingClientResults.length === 0 && (
                          <div className="absolute z-50 mt-1 w-full bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] rounded-lg shadow-lg p-3">
                            <p className="text-xs text-[#787774] mb-2">Nessun risultato</p>
                            <button
                              type="button"
                              onClick={() => {
                                setBookingManualEntry(true);
                                setBookingClientDropdownOpen(false);
                                setBookerName(bookingClientSearch);
                                setBookingClientSearch('');
                              }}
                              className="flex items-center gap-1.5 text-sm text-[#2383E2] hover:underline"
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                              Inserisci manualmente
                            </button>
                          </div>
                        )}
                        {/* Quick manual entry button */}
                        <button
                          type="button"
                          onClick={() => {
                            setBookingManualEntry(true);
                            setBookingClientDropdownOpen(false);
                            setBookingClientSearch('');
                          }}
                          className="mt-1.5 flex items-center gap-1.5 text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors"
                        >
                          <UserPlus className="w-3 h-3" />
                          Inserisci manualmente
                        </button>
                      </div>
                    )}
                  </PropertyRow>

                  {/* Booker Name */}
                  <PropertyRow icon={User} label="Nome partecipante" error={errors.bookerName}>
                    <input
                      type="text"
                      value={bookerName}
                      onChange={(e) => setBookerName(e.target.value)}
                      placeholder="Nome e cognome..."
                      className={inputClass}
                      readOnly={!!bookingClientId}
                    />
                  </PropertyRow>

                  {/* Booker Email */}
                  <PropertyRow icon={Mail} label="Email partecipante" error={errors.bookerEmail}>
                    <input
                      type="email"
                      value={bookerEmail}
                      onChange={(e) => setBookerEmail(e.target.value)}
                      placeholder="email@esempio.it"
                      className={inputClass}
                      readOnly={!!bookingClientId}
                    />
                  </PropertyRow>

                  {/* Booker Phone */}
                  <PropertyRow icon={Phone} label="Telefono (opzionale)">
                    <input
                      type="tel"
                      value={bookerPhone}
                      onChange={(e) => setBookerPhone(e.target.value)}
                      placeholder="+39..."
                      className={inputClass}
                      readOnly={!!bookingClientId}
                    />
                  </PropertyRow>

                  {/* Booker Notes */}
                  <PropertyRow icon={FileText} label="Note partecipante (opzionale)">
                    <textarea
                      value={bookerNotes}
                      onChange={(e) => setBookerNotes(e.target.value)}
                      placeholder="Note del partecipante..."
                      rows={2}
                      className={`${inputClass} resize-none`}
                    />
                  </PropertyRow>

                  {/* Internal Notes */}
                  <PropertyRow icon={StickyNote} label="Note interne (opzionale)">
                    <textarea
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      placeholder="Note visibili solo al team..."
                      rows={2}
                      className={`${inputClass} resize-none`}
                    />
                  </PropertyRow>
                </>
              ) : (
                <>
                  {/* ═══════════════════════════════════════════════ */}
                  {/* EVENT MODE (existing)                          */}
                  {/* ═══════════════════════════════════════════════ */}

                  {/* Title - Large input */}
                  <div className="py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={t.titlePlaceholder || "Titolo evento..."}
                      className={`w-full text-xl font-semibold text-[#37352F] dark:text-white placeholder-[#787774] dark:placeholder-gray-500 bg-transparent border-0 outline-none focus:ring-0 ${errors.title ? 'placeholder-red-400' : ''}`}
                    />
                    {errors.title && (
                      <p className="text-xs text-red-500 mt-1">{errors.title}</p>
                    )}
                  </div>

                  {/* All Day Toggle */}
                  <div className="py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={allDay}
                          onChange={(e) => setAllDay(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-9 h-5 rounded-full transition-colors ${
                          allDay ? 'bg-[#2383E2]' : 'bg-[rgba(55,53,47,0.16)] dark:bg-gray-600'
                        }`}>
                          <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                            allDay ? 'translate-x-4' : 'translate-x-0.5'
                          } mt-0.5`} />
                        </div>
                      </div>
                      <span className="text-sm text-[#37352F] dark:text-white">
                        {t.allDayEvent || 'Evento di tutta la giornata'}
                      </span>
                    </label>
                  </div>

                  {/* Date & Time */}
                  <PropertyRow icon={Clock} label={t.dateTime || "Data e Ora"}>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 mb-1">{t.start || 'Inizio'}</div>
                          <DatePicker
                            selected={startTime}
                            onChange={handleStartTimeChange}
                            showTimeSelect={!allDay}
                            dateFormat={allDay ? "d MMM yyyy" : "d MMM yyyy HH:mm"}
                            locale="it"
                            popperPlacement="bottom-start"
                            popperModifiers={[flip(), shift({ padding: 8 })]}
                            className={inputClass}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-[10px] text-[#787774] dark:text-gray-400 mb-1">{t.end || 'Fine'}</div>
                          <DatePicker
                            selected={endTime}
                            onChange={(date: Date) => setEndTime(date)}
                            showTimeSelect={!allDay}
                            dateFormat={allDay ? "d MMM yyyy" : "d MMM yyyy HH:mm"}
                            locale="it"
                            minDate={startTime}
                            popperPlacement="bottom-start"
                            popperModifiers={[flip(), shift({ padding: 8 })]}
                            className={`${inputClass} ${errors.endTime ? '!border-red-400' : ''}`}
                          />
                        </div>
                      </div>
                      {errors.endTime && (
                        <p className="text-xs text-red-500">{errors.endTime}</p>
                      )}
                    </div>
                  </PropertyRow>

                  {/* Location */}
                  <PropertyRow icon={MapPin} label={t.location || "Luogo"}>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder={t.locationPlaceholder || "Dove si svolgerà l'evento?"}
                      className="w-full px-2 py-1.5 text-sm text-[#37352F] dark:text-white placeholder-[#787774] dark:placeholder-gray-500 bg-transparent border border-transparent hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 focus:bg-[#FBFBFA] dark:focus:bg-[#3F4447] focus:border-[rgba(55,53,47,0.16)] dark:focus:border-[#4F5457] rounded outline-none transition-all"
                    />
                  </PropertyRow>

                  {/* Description */}
                  <PropertyRow icon={FileText} label={t.description || "Descrizione"}>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t.descriptionPlaceholder || "Aggiungi dettagli sull'evento..."}
                      rows={3}
                      className="w-full px-2 py-1.5 text-sm text-[#37352F] dark:text-white placeholder-[#787774] dark:placeholder-gray-500 bg-transparent border border-transparent hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 focus:bg-[#FBFBFA] dark:focus:bg-[#3F4447] focus:border-[rgba(55,53,47,0.16)] dark:focus:border-[#4F5457] rounded outline-none transition-all resize-none"
                    />
                  </PropertyRow>

                  {/* AlfaDocs Clinical Type */}
                  {alfadocsActive && alfadocsSyncEnabled && alfadocsTypes.length > 0 && (
                    <PropertyRow icon={Stethoscope} label={alfadocsLabel}>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setAlfadocsEventType(null)}
                          className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                            alfadocsEventType === null
                              ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                              : 'border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] text-[#37352F] dark:text-gray-300 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-[#2C2C2C]'
                          }`}
                        >
                          Nessuno
                        </button>
                        {alfadocsTypes.map((opt: any) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAlfadocsEventType(opt.value)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                              alfadocsEventType === opt.value
                                ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                                : 'border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] text-[#37352F] dark:text-gray-300 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-[#2C2C2C]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {alfadocsEventType && (
                        <>
                          <div className="mt-3">
                            <label className="text-[11px] font-medium text-[#787774] dark:text-gray-500 block mb-1.5">Paziente AlfaDocs</label>
                            <select
                              value={alfadocsClientId}
                              onChange={(e) => setAlfadocsClientId(e.target.value)}
                              className="w-full px-3 py-2 text-sm rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] bg-white dark:bg-[#25282B] text-[#37352F] dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            >
                              <option value="">Seleziona paziente...</option>
                              {alfadocsClients.map(c => (
                                <option key={c.value} value={c.value}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                          {!alfadocsClientId && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                              Seleziona un paziente collegato ad AlfaDocs per sincronizzare l'appuntamento
                            </p>
                          )}
                          {alfadocsClientId && (
                            <p className="text-[11px] text-[#787774] dark:text-gray-500 mt-1.5">
                              L'appuntamento verrà sincronizzato su AlfaDocs
                            </p>
                          )}
                        </>
                      )}
                    </PropertyRow>
                  )}

                  {/* Google Calendar Options */}
                  {isGoogleCalendar && (
                    <>
                      {/* Save to Google Calendar Toggle */}
                      <div className="py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <div className="flex items-center justify-center w-6 h-6 mt-0.5">
                            <Calendar className="w-4 h-4 text-[#2383E2]" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  checked={saveToGoogleCalendar}
                                  onChange={(e) => setSaveToGoogleCalendar(e.target.checked)}
                                  className="sr-only"
                                />
                                <div className={`w-9 h-5 rounded-full transition-colors ${
                                  saveToGoogleCalendar ? 'bg-[#2383E2]' : 'bg-[rgba(55,53,47,0.16)] dark:bg-gray-600'
                                }`}>
                                  <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                                    saveToGoogleCalendar ? 'translate-x-4' : 'translate-x-0.5'
                                  } mt-0.5`} />
                                </div>
                              </div>
                              <span className="text-sm text-[#37352F] dark:text-white">
                                Salva su Google Calendar
                              </span>
                            </div>
                          </div>
                        </label>
                      </div>

                      {/* Google Calendar Selector */}
                      {saveToGoogleCalendar && googleCalendars.length > 0 && (
                        <div className="py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                          <div className="flex items-start gap-3">
                            <div className="flex items-center justify-center w-6 h-6 mt-0.5">
                              <Calendar className="w-4 h-4 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1">
                              <div className="text-[10px] text-[#787774] dark:text-gray-400 mb-1.5 uppercase tracking-wide font-medium">
                                Calendario di destinazione
                              </div>
                              <div className="relative">
                                <select
                                  value={selectedGoogleCalendarId}
                                  onChange={(e) => setSelectedGoogleCalendarId(e.target.value)}
                                  className="w-full px-3 py-2 pr-8 text-sm text-[#37352F] dark:text-white bg-[#FBFBFA] dark:bg-[#3F4447] border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] rounded-md focus:border-[#2383E2] focus:ring-1 focus:ring-[#2383E2] outline-none transition-colors appearance-none cursor-pointer"
                                >
                                  {googleCalendars.map((cal) => (
                                    <option key={cal.id} value={cal.googleCalendarId}>
                                      {cal.name}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#787774] dark:text-gray-400 pointer-events-none" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Google Meet Toggle */}
                      {!allDay && saveToGoogleCalendar && (
                        <div className="py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <div className="flex items-center justify-center w-6 h-6 mt-0.5">
                              <Video className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <input
                                    type="checkbox"
                                    checked={addGoogleMeet}
                                    onChange={(e) => setAddGoogleMeet(e.target.checked)}
                                    className="sr-only"
                                  />
                                  <div className={`w-9 h-5 rounded-full transition-colors ${
                                    addGoogleMeet ? 'bg-green-600' : 'bg-[rgba(55,53,47,0.16)] dark:bg-gray-600'
                                  }`}>
                                    <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                                      addGoogleMeet ? 'translate-x-4' : 'translate-x-0.5'
                                    } mt-0.5`} />
                                  </div>
                                </div>
                                <span className="text-sm text-[#37352F] dark:text-white">
                                  Google Meet
                                </span>
                              </div>
                              {addGoogleMeet && (
                                <div className="mt-2 flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400">
                                  <Zap className="w-3 h-3" />
                                  <span>Un link sarà generato automaticamente</span>
                                </div>
                              )}
                            </div>
                          </label>
                        </div>
                      )}

                      {/* Invite Emails */}
                      {saveToGoogleCalendar && (
                        <PropertyRow icon={Users} label={t.participants || "Partecipanti"}>
                          <EmailAutoComplete
                            selectedEmails={inviteEmails}
                            onEmailsChange={setInviteEmails}
                            placeholder={t.addParticipants || "Aggiungi partecipanti per email..."}
                            className="w-full"
                          />
                          {inviteEmails.length > 0 && (
                            <div className="mt-2 text-xs text-[#787774] dark:text-gray-400">
                              {inviteEmails.length} {t.willReceiveInvite || 'partecipante/i riceverà un invito email'}
                            </div>
                          )}
                        </PropertyRow>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
              {rescheduleFromGoogle && (
                <label className="flex items-center gap-2 mb-3 text-sm text-[#37352F] dark:text-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteOriginalGoogle}
                    onChange={(e) => setDeleteOriginalGoogle(e.target.checked)}
                    className="rounded border-[rgba(55,53,47,0.3)]"
                  />
                  <span>Rimuovi l'evento originale da Google Calendar</span>
                </label>
              )}
              {/* Pulsanti come nel resto del CRM: NotionButton, principale nero */}
              <div className="flex justify-end gap-2">
                <NotionButton type="button" variant="ghost" onClick={onClose}>
                  {t.cancel || 'Annulla'}
                </NotionButton>
                {eventMode === 'unavailable' && !isEditing ? (
                  <NotionButton
                    type="submit"
                    form="event-drawer-unavailability"
                    variant="primary"
                    loading={unavailabilitySaving}
                    icon={<CalendarOff className="w-4 h-4" />}
                  >
                    Segna non disponibile
                  </NotionButton>
                ) : (
                  <NotionButton
                    type="button"
                    variant="primary"
                    onClick={handleSubmit}
                    loading={isSaving}
                    disabled={!!(alfadocsEventType && !alfadocsClientId) || !!(selectedSlotConflict && !ignoreConflict)}
                    icon={<Save className="w-4 h-4" />}
                  >
                    {eventMode === 'booking'
                      ? 'Crea Prenotazione'
                      : isEditing ? (t.save || 'Salva') : (t.create || 'Crea Evento')}
                  </NotionButton>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default EventFormDrawer;
